import { supabaseClient } from '../supabase'
import baseDataProvider from '../supabase/baseDataProvider'
import { getItemFromLocalStorage } from '../common/LocalStorage'

const removeDuplicates = (data, source = 'unknown') => {
  const seenIds = new Set()
  const duplicateIds = []
  const uniqueData = []

  data.forEach(lead => {
    if (seenIds.has(lead.id)) {
      duplicateIds.push(lead.id)
    } else {
      seenIds.add(lead.id)
      uniqueData.push(lead)
    }
  })

  if (duplicateIds.length > 0) {
    console.warn(`⚠️ Removed ${duplicateIds.length} duplicate IDs from ${source}`)
  }

  return uniqueData
}

// Enhanced dataProvider with linked leads support
const leadsDataProvider = {
  ...baseDataProvider,

  getList: async (resource, params) => {
    const { filter, pagination, sort, meta } = params
    const { page, perPage } = pagination
    const { field, order } = sort
    const localCurrentYear = getItemFromLocalStorage('localCurrentYear')
    const isCurrentYear = !localCurrentYear || localCurrentYear?.id === process.env.REACT_APP_CURRENT_YEAR_ID

    // Past year: use leads_history_view with year_id filter
    if (!isCurrentYear) {
      const { current_year_id, show_all_history, groupFilters, ...restFilter } = filter || {}
      const historyFilter = { ...restFilter, year_id: localCurrentYear.id }

      const areFiltersPaused = getItemFromLocalStorage('pauseFilters') ?? false
      const advancedFilters = areFiltersPaused ? null : (getItemFromLocalStorage('advanced_filters') ?? null)

      // Use same simple-filter check as current year: anything beyond these needs the RPC
      const historySimpleFilters = ['year_id', 'parent', 'stop_rgpd']
      const hasOnlySimpleFilters = Object.keys(historyFilter).every(key =>
        historySimpleFilters.includes(key)
      )

      // Route through RPC for advanced filters OR non-trivial basic filters (phone, email, center…)
      if (advancedFilters || !hasOnlySimpleFilters) {
        if (!show_all_history) {
          historyFilter.exclude_current_year_id = process.env.REACT_APP_CURRENT_YEAR_ID
        }

        const { data, error } = await supabaseClient.rpc('rpc_get_leads_with_filters', {
          p_group_filters: advancedFilters,
          p_basic_filters: historyFilter,
          p_order_by: field !== 'updated_at' ? field : 'updated_at',
          p_order_direction: order === 'ASC' ? 'asc' : 'desc',
          p_limit: perPage,
          p_offset: (page - 1) * perPage,
          p_resource: 'leads_history_view'
        })

        if (error) {
          console.error('RPC Error:', error)
          throw new Error(error.message)
        }

        const leads = data ? data.map(row => row.lead_data) : []
        const total = data && data.length > 0 ? data[0].total_count : 0

        return {
          data: removeDuplicates(leads),
          total: parseInt(total)
        }
      }

      // Simple filters only: direct query on leads_history_view
      if (!show_all_history) {
        historyFilter['current_year_id@neq'] = process.env.REACT_APP_CURRENT_YEAR_ID
      }

      const historyParams = { ...params, filter: historyFilter }
      return baseDataProvider.getList('leads_history_view', historyParams)
    }

    // Current year: existing logic (unchanged)
    const areFiltersPaused = getItemFromLocalStorage('pauseFilters') ?? false
    const advancedFilters = areFiltersPaused ? null : (getItemFromLocalStorage('advanced_filters') ?? null)

    const allowedSimpleFilters = ['current_year_id', 'parent', 'stop_rgpd']
    const hasOnlySimpleFilters = filter && Object.keys(filter).every(key =>
      allowedSimpleFilters.includes(key) ||
      (key === 'groupFilters' && filter[key] !== 'on')
    )

    resource = 'leads'

    if (!filter || (hasOnlySimpleFilters && !advancedFilters)) {
      if (params.filter?.groupFilters) {
        delete params.filter.groupFilters
      }

      return baseDataProvider.getList(resource, params)
    }

    // use RPC for all filters even basic ones
    const { data, error } = await supabaseClient.rpc('rpc_get_leads_with_filters', {
      p_group_filters: advancedFilters,
      p_basic_filters: filter,
      p_order_by: field !== 'updated_at'
        ? field
        : (filter.duplicate_leads === true ? 'phone' : 'updated_at'),
      p_order_direction: order === 'ASC' ? 'asc' : 'desc',
      p_limit: perPage,
      p_offset: (page - 1) * perPage,
      p_resource: resource
    })

    if (error) {
      console.error('RPC Error:', error)
      throw new Error(error.message)
    }

    const leads = data ? data.map(row => row.lead_data) : []
    const total = data && data.length > 0 ? data[0].total_count : 0

    return {
      data: removeDuplicates(leads),
      total: parseInt(total)
    }
  },

  getOne: async (resource, params) => {
    const localCurrentYear = getItemFromLocalStorage('localCurrentYear')
    const isCurrentYear = !localCurrentYear || localCurrentYear?.id === process.env.REACT_APP_CURRENT_YEAR_ID

    if (resource === 'leads' && !isCurrentYear) {
      const { data, error } = await supabaseClient
        .from('leads_history_view')
        .select('*')
        .eq('id', params.id)
        .eq('year_id', localCurrentYear.id)
        .single()

      if (error) throw new Error(error.message)
      return { data }
    }

    return baseDataProvider.getOne(resource, params)
  },

  deleteMany: async (resource, params) => {
    const chunkSize = 100
    const chunks = []

    for (let i = 0; i < params.ids.length; i += chunkSize) {
      chunks.push(params.ids.slice(i, i + chunkSize))
    }

    const errors = []

    for (const chunk of chunks) {
      const { error } = await supabaseClient
        .from(resource)
        .delete()
        .in('id', chunk)
      if (error) {
        errors.push(error)
      }
    }

    if (errors.length > 0) {
      throw new Error(`Errors occurred in deleteMany: ${errors.map(e => e.message).join(', ')}`)
    }

    return { data: params.ids }
  }
}

export const dataProvider = leadsDataProvider
